import { makeAutoObservable, reaction } from 'mobx';
import type { RailTab, PageAction } from '@/features/layout/types';
import { loadRailWidth, saveRailWidth, RAIL_WIDTH_MIN, RAIL_WIDTH_MAX } from '@/features/layout/logic';
import { entityStore } from './EntityStore';

class RailStore {
  isOpen = false;
  activeTab: RailTab = 'resources';
  width: number;
  /** Quick actions of the open page (see usePageActions). */
  pageActions: PageAction[] = [];

  constructor() {
    makeAutoObservable(this);
    this.width = loadRailWidth();
    // A panel with nothing in it must not be reachable: the moment the open
    // page stops offering anything, the rail closes and its toggle goes away.
    reaction(
      () => this.hasContent,
      (hasContent) => {
        if (!hasContent) this.isOpen = false;
      },
    );
  }

  /**
   * Whether the rail has anything to show on the current route.
   *
   * Every tab draws from one of two sources: quick actions the page
   * registered, or the entity the page put into context. With neither, the
   * panel can only say "select an item" — so it does not open at all.
   */
  get hasContent(): boolean {
    return this.pageActions.length > 0 || entityStore.currentEntity !== null;
  }

  toggle() {
    if (!this.isOpen && !this.hasContent) return;
    this.isOpen = !this.isOpen;
  }

  open() {
    if (!this.hasContent) return;
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  setTab(tab: RailTab) {
    this.activeTab = tab;
    if (!this.isOpen && this.hasContent) this.isOpen = true;
  }

  resize(width: number) {
    this.width = Math.min(RAIL_WIDTH_MAX, Math.max(RAIL_WIDTH_MIN, width));
    saveRailWidth(this.width);
  }

  setPageActions(actions: PageAction[]) {
    this.pageActions = actions;
  }

  clearPageActions() {
    this.pageActions = [];
    if (this.activeTab === 'actions') this.activeTab = 'resources';
  }
}

export const railStore = new RailStore();
